import {useAuth} from '@workos-inc/authkit-react';
import {useCallback} from 'react';
import {variables as apiVariables} from '@/environment/api';
import {ApiError, ApiErrorData} from '@/features/core/api/api-errors';
import {queryKeys} from '@/features/core/api/api-hooks';
import {UserInfo, Permission, hasPermission as checkPermission} from '../types/user';
import {useAuthQuery} from './use-auth-query';

/**
 * Interface for the return value of the useUserInfo hook
 * Explicitly defining this makes the hook's contract clear to consumers
 */
type UseUserInfoResult = {
  userInfo: UserInfo | null;
  isLoading: boolean;
  error: Error | null;
  hasPermission: (permission: Permission) => boolean;
};

/**
 * Hook for accessing user information and permissions
 * This hook depends on auth being ready and will only fetch when authenticated
 * @returns {UseUserInfoResult} User information and utility functions
 */
export function useUserInfo(): UseUserInfoResult {
  const {user, isLoading: authLoading} = useAuth();

  // Determine if user is authenticated based on user object
  const isAuthenticated = Boolean(user);

  // Use a stable query key for better caching
  const queryKey = queryKeys.userInfo;

  const fetchUserInfo = async (token: string): Promise<UserInfo> => {
    const apiBaseUrl = apiVariables.RPC_BASE_URL.value;
    const controller = new AbortController();
    const {signal} = controller;

    try {
      const response = await fetch(`${apiBaseUrl}/api/auth/userinfo`, {
        headers: {Authorization: `Bearer ${token}`},
        signal,
      });

      if (!response.ok) {
        // Try to parse error response as JSON
        let errorData: ApiErrorData = null;
        try {
          errorData = (await response.json()) as Record<string, unknown>;
        } catch {
          // If JSON parsing fails, try to get text
          try {
            errorData = await response.text();
          } catch {
            // If both fail, leave as null
          }
        }

        throw ApiError.fromResponse(response, errorData);
      }

      return (await response.json()) as UserInfo;
    } catch (error) {
      // Handle different error types
      if (error instanceof ApiError) {
        throw error;
      } else if (error instanceof TypeError && error.message.includes('network')) {
        throw ApiError.networkError(error);
      } else if (error instanceof DOMException && error.name === 'AbortError') {
        throw new ApiError({
          message: 'Request was aborted',
          isNetworkError: true,
        });
      } else if (error instanceof Error) {
        throw ApiError.networkError(error);
      }

      throw new ApiError({message: String(error)});
    }
  };

  const {
    data: userInfo,
    isLoading: queryLoading,
    error,
  } = useAuthQuery<UserInfo>(queryKey, fetchUserInfo, {
    enabled: isAuthenticated && !authLoading,
    refetchOnMount: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30_000),
  });

  // Consolidate loading states
  const isLoading = authLoading || queryLoading;

  // Permission helpers with proper type safety
  const hasPermission = useCallback(
    (permission: Permission): boolean => {
      if (isLoading || !userInfo) {
        return false;
      }

      return checkPermission(userInfo, permission);
    },
    [userInfo, isLoading],
  );

  return {
    userInfo: isLoading ? null : (userInfo ?? null),
    isLoading,
    error: error ?? null,
    hasPermission,
  };
}
